'use client';

import { CancerTypeAutocomplete } from '@/layers/presentation/components/common/CancerTypeAutocomplete';
import PatientParamsForm from '@/layers/presentation/components/patients/PatientParamsForm';
import { Select, SelectItem } from '@/layers/presentation/components/ui/Select';
import { useGlobalState } from '@/layers/presentation/context/GlobalStateContext';
import {
  HandleParamChangeType,
  HandleQueryChangeType,
  useProgramSearch,
} from '@/layers/presentation/hooks';
import { useTranslation } from '@/layers/presentation/i18n/client';
import { useOnboardingContext } from '@/layers/presentation/providers/OnboardingProvider';
import { PatientParamValues } from '@/types/models';
import { Button, Tab, Tabs } from '@nextui-org/react';
import React, { useCallback, useState } from 'react';

interface ProgramSearchFiltersProps {
  filters: {
    patientParams: PatientParamValues;
    query: string | undefined;
  };
  onParamChange: HandleParamChangeType;
  onQueryChange: HandleQueryChangeType;
}

const ProgramSearchFilters: React.FC<ProgramSearchFiltersProps> = ({
  filters,
  onParamChange,
  onQueryChange,
}) => {
  const { t } = useTranslation();
  const { user } = useGlobalState();
  const onboardingContext = useOnboardingContext();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const { startOnboarding, shouldShowSearchBarUpload } = onboardingContext || {};

  const {
    savedSearches,
    selectedSearch,
    selectedTabKey,
    patientParamsDictionary,
    isLoadingParams,
    handleSaveSearch,
    handleSearchSelect,
    formatSearchName,
    setSelectedTabKey,
    isAdvancedSearchVisible,
    toggleAdvancedSearch,
    isSaving,
  } = useProgramSearch();

  const handleQueryChangeWrapper = useCallback(
    (value: string) => {
      // Only update if the field is focused or we're clearing the value
      if (isInputFocused || value === '') {
        onQueryChange({ query: value });
      }
    },
    [onQueryChange, isInputFocused],
  );

  const handleUploadPromo = useCallback(() => {
    try {
      startOnboarding();
    } catch (error) {
      // Fallback: redirect manually if there's an error
      window.location.href = '/users/auth/patient?signup=true';
    }
  }, [startOnboarding]);

  const renderSearchFields = () => {
    return (
      <div>
        <CancerTypeAutocomplete
          query={filters.query || ''}
          onQueryChange={handleQueryChangeWrapper}
          patientParamsDictionary={patientParamsDictionary}
          shouldShowUploadPromo={shouldShowSearchBarUpload && !!onboardingContext}
          onUploadPromo={handleUploadPromo}
          minQueryLength={3}
          label={t('components:programSearchFilters.searchQuery')}
          placeholder={t('components:programSearchFilters.searchPlaceholder')}
          className="mt-2 mb-4 w-full"
          inputValue={filters.query || ''}
          data-testid="programs-search-input"
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
        />

        {/* Advanced Search Button */}
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="bordered"
            onClick={toggleAdvancedSearch}
            aria-expanded={isAdvancedSearchVisible}
            aria-controls="advanced-search-form"
          >
            {isAdvancedSearchVisible
              ? t('components:programSearchFilters.hideAdvancedSearch')
              : t('components:programSearchFilters.showAdvancedSearch')}
          </Button>
        </div>

        {/* PatientParamsForm: Hidden by default */}
        {isAdvancedSearchVisible && (
          <div className="mt-4 mb-8">
            <h3 className="mb-2">Advanced Search</h3>
            <PatientParamsForm
              mode="filter"
              isHideGenderAndAge={true}
              patientParamsDictionary={patientParamsDictionary}
              selectedOptions={filters.patientParams || {}}
              onParamChange={onParamChange}
              isLoading={isLoadingParams}
            />
          </div>
        )}
      </div>
    );
  };

  if (!user) {
    return renderSearchFields();
  }

  return (
    <div>
      <div className="flex flex-row items-center justify-end space-x-4 mb-4">
        <Button
          size="sm"
          radius="sm"
          variant="bordered"
          className="border-primary min-w-[100px]"
          onPress={() => handleSaveSearch(filters.patientParams || {}, filters.query || '')}
          isLoading={isSaving}
        >
          {t('components:programSearchFilters.save')}
        </Button>
        <Tabs
          aria-label={t('components:programSearchFilters.options')}
          selectedKey={selectedTabKey}
          onSelectionChange={key => setSelectedTabKey(key?.toString() || '')}
          classNames={{
            tabList: ['mb-0', 'bg-secondary'],
            cursor: ['shadow-none', 'group-data-[selected=true]:bg-primary'],
            tab: [],
            tabContent: ['text-black', 'group-data-[selected=true]:text-white'],
            panel: 'w-full',
          }}
        >
          <Tab key="search" title={t('components:programSearchFilters.search')} />
          <Tab key="saved-searches" title={t('components:programSearchFilters.savedSearches')} />
          {/* <Tab key="bookmarks" title={t('components:programSearchFilters.bookmarks')} /> */}
        </Tabs>
      </div>

      <div className="mt-4">
        {/* {selectedTabKey === "search" && (
          renderSearchFields()
        )} */}
        {selectedTabKey === 'saved-searches' && (
          <>
            <Select
              aria-label={t('components:programSearchFilters.savedSearches')}
              className="mb-8"
              placeholder={t('components:programSearchFilters.selectSavedSearch')}
              value={selectedSearch}
              onChange={e => handleSearchSelect(e.target.value)}
              classNames={{
                trigger: 'h-14',
              }}
            >
              {savedSearches.map(search => (
                <SelectItem
                  key={search.id}
                  value={search.id.toString()}
                  textValue={formatSearchName(search)}
                >
                  {formatSearchName(search)}
                </SelectItem>
              ))}
            </Select>
          </>
        )}
        {renderSearchFields()}
        {selectedTabKey === 'bookmarks' && (
          <div>{t('components:programSearchFilters.bookmarksContent')}</div>
        )}
      </div>
    </div>
  );
};

export default ProgramSearchFilters;
